import type { Changeset, Dataset } from '@vero/domain';

export interface SnapshotMeta {
  id: string;
  label: string;
  reason: 'pre-import' | 'pre-restore' | 'pre-rollback' | 'manual' | 'auto';
  createdAt: string;
  records: number;
  bytes: number;
  checksum: string;
}

/**
 * Storage adapter contract. Every implementation must be transactional:
 * `commit` and `replaceAll` either apply completely or not at all.
 * Implementations: MemoryAdapter (tests), SqliteAdapter (Node / desktop), IdbAdapter (browser / webview).
 * All of them pass tests/integration/adapter-conformance.test.ts.
 */
/** A stored document file (blob). Content lives outside the dataset so it is never loaded eagerly. */
export interface FileMeta { id: string; name: string; mime: string; size: number; sha256: string; createdAt: string }

export interface StorageAdapter {
  readonly engine: string;
  /** open the store and run pending schema migrations; returns the schema version now in effect */
  open(): Promise<{ schemaVersion: number; created: boolean }>;
  loadAll(): Promise<Dataset>;
  commit(cs: Changeset): Promise<void>;
  replaceAll(ds: Dataset): Promise<void>;
  getMeta<T = unknown>(key: string): Promise<T | undefined>;
  setMeta(key: string, value: unknown): Promise<void>;
  saveSnapshot(meta: SnapshotMeta, content: string): Promise<void>;
  listSnapshots(): Promise<SnapshotMeta[]>;
  readSnapshot(id: string): Promise<string | undefined>;
  deleteSnapshot(id: string): Promise<void>;
  /** document vault (schema v4): content-addressed by the caller, stored transactionally */
  putFile(meta: FileMeta, content: Uint8Array): Promise<void>;
  getFile(id: string): Promise<{ meta: FileMeta; content: Uint8Array } | undefined>;
  deleteFile(id: string): Promise<void>;
  listFiles(): Promise<FileMeta[]>;
  close(): Promise<void>;
}

export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
export function fromBase64(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export class StorageError extends Error {
  code: string;
  constructor(code: string, message?: string, options?: { cause?: unknown }) { super(message ?? code, options); this.code = code; }
}
